import React, { useState } from "react";
import { Dimmer, Loader } from "semantic-ui-react";
import { useSelector } from "react-redux";
import { selectTranslations } from "../../config/i18n/slice";
import { toast } from "react-toastify";
import { CloseIcon } from "../../../Assets/Icons/svg";
import ToolTip from "react-power-tooltip";
import location_icon from "../../../Assets/Icons/location_on.jpg";
import sub_location_icon from "../../../Assets/Icons/pin_drop.png";
import SetForm from "./SetForm";
import { cloneDeep } from "lodash";
import useNewCancelModal from "../../../hooks/Messages/useNewCncelModal";
import { useEffect } from "react";
import { Resizable } from "re-resizable";
import { getAllParts } from "../../config/sharedApi/partApis";
import { fetchInvLocationsOptions } from "../../config/sharedApi/workOrderApis";
import { currencyDecimalPlaces } from "../../config/common/commonVariables";
import { getAllActiveSets, updateSet } from "../../config/api/setApis";

/*
 * The Edit function in JavaScript is used to handle editing sets and parts data, including adding, updating, and canceling changes.
 */
function Edit({
  toggled,
  untoggle,
  setRefresh,
  refresh,
  targetedData,
  role,
  allIds,
}) {
  const t = useSelector(selectTranslations);
  const organisationId = useSelector((state) => {
    if (state?.auth?.user?.organisation) {
      return state.auth.user.organisation.id;
    } else {
      return null;
    }
  });

  const organisationCurrency = useSelector((state) => {
    if (state?.auth?.user?.organisation) {
      return state.auth.user.organisation.currency;
    } else {
      return "JOD";
    }
  });

  const handleDecimalPoints = (value) => {
    const decimalPlaces = currencyDecimalPlaces[organisationCurrency] ?? 2; // Default to 2 if currency not found
    const factor = Math.pow(10, decimalPlaces);
    const roundedValue = Math.round(value * factor) / factor;
    const formattedValue = roundedValue.toFixed(decimalPlaces);

    // Remove trailing zeros after decimal point
    if (formattedValue.indexOf(".") !== -1) {
      return formattedValue.replace(/\.?0+$/, "");
    }

    return Number(formattedValue);
  };

  const [dropdownOptions, setDropdownOptions] = useState({
    locations: [],
  });
  const [partsData, setPartsData] = useState([]);
  const [modalWidth, setModalWidth] = useState(
    Math.max(window.innerWidth / 2, 514)
  );
  const [allPartsData, setAllPartsData] = useState([]);
  const [prevPartsData, setPrevPartsData] = useState([]);
  const [prevSetsData, setPrevSetsData] = useState([]);
  const [allSetsData, setAllSetsData] = useState([]);
  const [selectedTablePartData, setSelectedTablePartData] = useState([]);
  const [selectedTableSetData, setSelectedTableSetData] = useState([]);
  const [addSetModal, setAddSetModal] = useState(false);
  const [initPartsData, setInitPartsData] = useState([]);
  const [initSetsData, setInitSetsData] = useState([]);

  const [setsData, setSetsData] = useState([]);

  const { setNewCancelModal, NewCancelModal } = useNewCancelModal();
  const [editSetData, setEditData] = useState();
  const [addPartModal, setAddPartModal] = useState(false);

  const selectedLanguage = useSelector((state) => {
    if (state?.i18n?.lang) {
      return state.i18n.lang;
    } else {
      return "en";
    }
  });

  const fetchLocations = async () => {
    const fetchData = await fetchInvLocationsOptions(organisationId);

    let parentOptions = [];

    if (fetchData?.length > 0) {
      fetchData.forEach((el) => {
        parentOptions.push({
          key: el.id,
          value: el.id,
          text: el.name,
          disabled: el?.disable,
          image: { src: location_icon },
          className: "main-location-dropdown-option",
        });

        if (el?.subLocations?.length > 0) {
          el.subLocations.forEach((subEl) => {
            parentOptions.push({
              key: subEl.id,
              value: subEl.id,
              text: subEl.name,
              image: { src: sub_location_icon },
              className: "sub-location-dropdown-option",
              parentId: el.id,
            });
          });
        }
      });

      setDropdownOptions((prev) => ({
        ...prev,
        locations: parentOptions,
      }));

      return parentOptions;
    }
  };

  /*
   * The `addParts` function retrieves all parts data, filters out selected parts, and sets the necessary state variables for further processing
   */
  const addParts = async (selectedPartsData = []) => {
    setInitChange(true);
    const fetchData = await getAllParts(organisationId, '', 1, 99999,[],[],[],[],[targetedData?.attributes?.location?.data?.id],[],[],[],'',[],role,[]);

    if (fetchData?.response) {
      const filteredParts = fetchData?.response?.flatMap((part) =>
        part?.attributes?.inventory?.length > 0
          ? part.attributes.inventory
              .filter(
                (inv) =>
                  inv?.location?.data?.id ==
                  targetedData?.attributes?.location?.data?.id
              )
              .map((inv) => ({
                id: part.id,
                name: part.attributes.name,
                totalCost: inv?.unitCost !== null ? inv?.unitCost : 0,
                quantity: 1,
                unitCost: inv?.unitCost !== null ? inv?.unitCost : 0,
                unitType: part?.attributes?.measuringUnit,
                selectedPart: false,
              }))
          : []
      );

      const filteredPartsData = filteredParts
        ?.map((part) => {
          const isSelected = selectedPartsData.some(
            (selectedPart) => selectedPart.id === part.id
          );

          if (isSelected) {
            return null; // Skip this part
          } else {
            return {
              selectedPart: false,
              id: part.id,
              name: part.name,
              totalCost: part.totalCost,
              unitType: part.unitType,
              quantity: 1,
              unitCost: part.unitCost,
            };
          }
        })
        .filter((part) => part !== null);

      setPartsData(filteredPartsData);
      setInitPartsData(filteredPartsData);
      setAllPartsData(filteredPartsData);
    } else {
      toast.error(t.common.something_wrong);
    }

    setAddPartModal(true);
  };

  /*
   * The function `addSets` retrieves all sets data, filters out selected sets, and updates the state with the filtered sets data.
   */
  const addSets = async (selectedSetsData = []) => {
    setInitChange(true);

    const fetchData = await getAllActiveSets(organisationId);

    if (fetchData) {
      const allInitData = fetchData.map((set) => ({
        id: set.id,
        name: set.name,
        totalCost: set.totalCost,
        quantity: 1,
        unitCost: set.totalCost,
        selectedSet: false,
        disable: set?.includeSet,
      }));

      const filteredSets = allInitData
        ?.map((part) => {
          const isSelected = selectedSetsData.some(
            (selectedPart) => selectedPart.id === part.id
          );

          if (isSelected) {
            return null;
          } else {
            return {
              selectedSet: false,
              id: part.id,
              name: part.name,
              disable: part?.disable,
              totalCost: part.totalCost,
              quantity: 1,
              unitCost: part.unitCost,
            };
          }
        })
        .filter((part) => part !== null);
      setSetsData(filteredSets);
      setInitSetsData(filteredSets);
      setAllSetsData(allInitData);
    } else {
      toast.error(t.common.something_wrong);
    }
    setAddSetModal(true);
  };

  /* The above code is a React useEffect hook that runs when the `toggled` state changes. It checks if
`toggled` is true and then formats the `targetedData` by mapping over the `parts` and `sets` arrays
within its attributes. */
  useEffect(() => {
    if (toggled) {
      fetchLocations();
      const formattedParts = targetedData?.attributes?.parts?.map((part) => {
        return {
          selectedPart: true,
          compId: part?.id,
          id: part?.part?.data?.id,
          name: part?.part?.data?.attributes?.name,
          totalCost: part?.totalCost,
          quantity: part?.quantity,
          unitType: part?.part?.data?.attributes?.measuringUnit,
          unitCost: handleDecimalPoints(
            Number(part?.totalCost) / Number(part?.quantity)
          ),
        };
      });

      const formattedSets = targetedData?.attributes?.sets?.map((part) => {
        return {
          selectedPart: true,
          compId: part?.id,
          id: part.set.data.id,
          name: part.set.data.attributes.name,
          totalCost: part.totalCost,
          quantity: part.quantity,
          unitCost: part.totalCost / part.quantity,
        };
      });

      addParts(formattedParts);
      setSelectedTablePartData(formattedParts);
      addSets(formattedSets);
      setSelectedTableSetData(formattedSets);
      setEditData({
        name: targetedData?.attributes?.name,
        locations: targetedData?.attributes?.location?.data?.id,
        parts: formattedParts,
        sets: formattedSets,
      });
    }
  }, [toggled]);

  const { initData } = useSelector((state) => ({
    initData: state.sets.initData,
  }));

  const [initChange, setInitChange] = useState(false);

  const [showCancleTooltip, setCancleTooltip] = useState(false);
  const [buttonStatus, setButtonStatus] = useState(0);

  /*
   * The `handleAddSet` function in JavaScript updates a set with new data after performing validation checks and update the sets.
   */
  const handleAddSet = async (values, resetForm) => {
    let addedSetsData = cloneDeep(values);
    const numParts = addedSetsData.parts.length;
    const numSets = addedSetsData.sets.length;

    if ((numParts === 1 && numSets === 1) || numSets >= 2 || numParts >= 2) {
      let partsId = [];
      const transformedArray = addedSetsData?.parts?.map((item) => {
        partsId.push(item.id);
        return {
          id: item?.compId,
          part: item.id,
          quantity: item.quantity,
          totalCost: item.totalCost,
        };
      });

      const transformedSetArray = addedSetsData?.sets?.map((item) => ({
        set: item.id,
        id: item?.compId,
        quantity: item.quantity,
        totalCost: item.totalCost,
      }));

      const response = await updateSet(
        targetedData?.id,
        addedSetsData?.name,
        transformedArray,
        transformedSetArray,
        organisationId,
        partsId,
        addedSetsData?.locations
      );

      if (response.status == 200) {
        setRefresh(!refresh);
        setButtonStatus(0);
        untoggle();
        toast.success(t.sets.update_success);
        resetForm();
      } else if (response.status == 400) {
        if (response.error && response.error.message) {
          toast.error(t.sets.unique);
        } else {
          toast.error(t.sets.unique);
        }
      } else {
        toast.error(t.common.something_wrong);
        untoggle();
      }
    } else {
      toast.error(t.sets.add_valiadtion);
    }
  };

  /*
   * The `cancelHandler` function adjusts modal width, toggles off a modal, and resets certain state
   * variables.
   */
  const cancelHandler = () => {
    setModalWidth(Math.max(window.innerWidth / 2, 514));
    untoggle();
    setNewCancelModal(false);
    setInitChange(false);
  };

  return (
    <>
      <div
        style={{ maxWidth: `${modalWidth}px` }}
        clearing
        className={
          toggled ? "aside-modal active procedure" : "aside-modal procedure"
        }
      >
        <Resizable
          size={{ width: modalWidth, height: "auto" }}
          minWidth={"514px"}
          onResize={(e, direction, ref, d) => {
            setModalWidth(ref.offsetWidth);
          }}
        >
          <div className="modal-header">
            <div className="flexbox justify-between">
              <div className="name">
                {t.sets.edit_set} #{targetedData?.attributes?.countId}
              </div>
              <div>
                <span
                  className="position-relative c-pointer"
                  onMouseOver={() => setCancleTooltip(true)}
                  onMouseLeave={() => setCancleTooltip(false)}
                  onClick={() =>
                    initChange ? setNewCancelModal(true) : cancelHandler()
                  }
                >
                  <CloseIcon />
                  <ToolTip
                    color="#fff"
                    backgroundColor="#172B4D"
                    position={
                      selectedLanguage == "en"
                        ? "bottom center"
                        : "bottom center"
                    }
                    arrowAlign="center"
                    show={showCancleTooltip}
                    textBoxWidth="36px"
                    padding="5px 5px 5px 7px"
                    borderRadius="5px"
                    static
                  >
                    <span className="work-order-preview-tooltip">
                      {t.common.close}
                    </span>
                  </ToolTip>
                </span>
              </div>
            </div>
          </div>

          {buttonStatus == 1 ? (
            <Dimmer active inverted>
              <Loader inverted content={t.common.loading} />
            </Dimmer>
          ) : (
            ""
          )}

          <SetForm
            fetchLocations={fetchLocations}
            initialFormValues={editSetData}
            initChange={initChange}
            allIds={allIds}
            dropdownOptions={dropdownOptions}
            role={role}
            setDropdownOptions={setDropdownOptions}
            setInitChange={setInitChange}
            buttonStatus={buttonStatus}
            cancelHandler={cancelHandler}
            setNewCancelModal={setNewCancelModal}
            handleAddSet={handleAddSet}
            setPartsData={setPartsData}
            partsData={partsData}
            setInitPartsData={setInitPartsData}
            initPartsData={initPartsData}
            setInitSetsData={setInitSetsData}
            initSetsData={initSetsData}
            setSetsData={setSetsData}
            setsData={setsData}
            edit={true}
            setAllSetsData={setAllSetsData}
            allSetsData={allSetsData}
            prevSetsData={prevSetsData}
            prevPartsData={prevPartsData}
            setPrevPartsData={setPrevPartsData}
            setPrevSetsData={setPrevSetsData}
            setSelectedTableSetData={setSelectedTableSetData}
            setSelectedTablePartData={setSelectedTablePartData}
            selectedTableSetData={selectedTableSetData}
            selectedTablePartData={selectedTablePartData}
            setAllPartsData={setAllPartsData}
            allPartsData={allPartsData}
          />
        </Resizable>
      </div>

      <div
        className={
          toggled ? "aside-modal-background active" : "aside-modal-background"
        }
        onClick={() => (initChange ? setNewCancelModal(true) : cancelHandler())}
      ></div>
      {/* cancel modal */}
      <NewCancelModal
        yesCallback={() => {
          cancelHandler();
        }}
        title={t.parts.form.changes}
      >
        <p className="cancel-child"> {t.common.cancel_2statement}</p>
      </NewCancelModal>
    </>
  );
}

export default Edit;
